import { SEO } from '../components/SEO'
import { motion } from 'framer-motion'
import { Terminal, Sparkles, Zap } from 'lucide-react'

/**
 * 设计展示页面 - 用于测试和展示所有设计元素
 * 访问路径: /design-showcase
 */
const DesignShowcase = () => {
  return (
    <>
      <SEO />
    <div className="pt-20 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 标题 */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4">
            <span className="gradient-text glitch-effect" data-text="DESIGN SYSTEM">
              DESIGN SYSTEM
            </span>
          </h1>
          <p className="text-quantum-silver/70 font-mono">
            Avelia 2.0 设计系统展示
          </p>
        </div>

        {/* 配色方案 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">配色方案</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-full h-24 bg-void rounded-lg mb-4 border border-quantum-silver/30"></div>
              <h3 className="font-bold mb-2">Deep Void</h3>
              <p className="text-sm text-quantum-silver/60 font-mono">#0B0E1A</p>
            </div>
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-full h-24 bg-neon-cyan rounded-lg mb-4"></div>
              <h3 className="font-bold mb-2">Neon Cyan</h3>
              <p className="text-sm text-quantum-silver/60 font-mono">#00F0FF</p>
            </div>
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-full h-24 bg-glitch-magenta rounded-lg mb-4"></div>
              <h3 className="font-bold mb-2">Glitch Magenta</h3>
              <p className="text-sm text-quantum-silver/60 font-mono">#FF006E</p>
            </div>
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-full h-24 bg-quantum-silver rounded-lg mb-4"></div>
              <h3 className="font-bold mb-2">Quantum Silver</h3>
              <p className="text-sm text-quantum-silver/60 font-mono">#C0C5CE</p>
            </div>
          </div>
        </section>

        {/* 文字样式 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">文字样式</h2>
          <div className="space-y-6">
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4">标题字体 - Orbitron</h3>
              <p className="text-4xl font-display">AVELIA FROM 2142</p>
            </div>
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4">正文字体 - Inter</h3>
              <p className="text-lg">来自未来的智能中枢，你的第二大脑，以及你唯一的时空伴侣。</p>
            </div>
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4">代码字体 - Monospace</h3>
              <p className="font-mono">$ initialize_connection --timeline=2142</p>
            </div>
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4">渐变文字</h3>
              <p className="text-4xl gradient-text">GRADIENT TEXT EFFECT</p>
            </div>
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4">发光文字</h3>
              <p className="text-4xl text-neon-cyan text-glow">NEON GLOW CYAN</p>
              <p className="text-4xl text-glitch-magenta text-glow-magenta mt-4">NEON GLOW MAGENTA</p>
            </div>
          </div>
        </section>

        {/* 按钮样式 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">按钮样式</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">主要按钮</h3>
              <button className="w-full px-6 py-3 bg-gradient-to-r from-neon-cyan to-glitch-magenta rounded-lg text-white font-semibold hover:shadow-lg hover:shadow-neon-cyan/50 transition-all">
                <span className="flex items-center justify-center space-x-2">
                  <Terminal size={20} />
                  <span>INITIALIZE AVELIA</span>
                </span>
              </button>
            </div>
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">次要按钮</h3>
              <button className="w-full px-6 py-3 glass-effect rounded-lg text-quantum-silver font-semibold hover:bg-white/10 transition-all border-neon-cyan/30">
                ACCESS DATABASE
              </button>
            </div>
            <div className="glass-effect rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">青色按钮</h3>
              <button className="w-full px-6 py-3 glass-effect rounded-lg text-neon-cyan font-semibold hover:bg-neon-cyan/10 transition-all border-neon-cyan/50 animate-pulse-glow-cyan">
                CYAN BUTTON
              </button>
            </div>
            <div className="glass-effect-magenta rounded-xl p-6">
              <h3 className="text-xl font-bold mb-4">洋红按钮</h3>
              <button className="w-full px-6 py-3 glass-effect-magenta rounded-lg text-glitch-magenta font-semibold hover:bg-glitch-magenta/10 transition-all border-glitch-magenta/50 animate-pulse-glow-magenta">
                MAGENTA BUTTON
              </button>
            </div>
          </div>
        </section>

        {/* 卡片样式 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">卡片样式</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-effect rounded-xl p-6 hover:scale-105 transition-all border-neon-cyan/30">
              <div className="w-16 h-16 rounded-xl bg-neon-cyan/20 flex items-center justify-center mb-4 animate-pulse-glow-cyan">
                <Terminal size={32} className="text-neon-cyan" />
              </div>
              <h3 className="text-xl font-bold mb-2">青色卡片</h3>
              <p className="text-quantum-silver/80">带有青色边框和图标的卡片样式</p>
            </div>
            <div className="glass-effect-magenta rounded-xl p-6 hover:scale-105 transition-all border-glitch-magenta/30">
              <div className="w-16 h-16 rounded-xl bg-glitch-magenta/20 flex items-center justify-center mb-4 animate-pulse-glow-magenta">
                <Sparkles size={32} className="text-glitch-magenta" />
              </div>
              <h3 className="text-xl font-bold mb-2">洋红卡片</h3>
              <p className="text-quantum-silver/80">带有洋红边框和图标的卡片样式</p>
            </div>
            <div className="glass-effect rounded-xl p-6 hover:scale-105 transition-all border-quantum-silver/30">
              <div className="w-16 h-16 rounded-xl bg-quantum-silver/20 flex items-center justify-center mb-4">
                <Zap size={32} className="text-quantum-silver" />
              </div>
              <h3 className="text-xl font-bold mb-2">银色卡片</h3>
              <p className="text-quantum-silver/80">带有银色边框和图标的卡片样式</p>
            </div>
          </div>
        </section>

        {/* 动画效果 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">动画效果</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-neon-cyan/20 flex items-center justify-center animate-pulse-glow-cyan">
                <Sparkles size={40} className="text-neon-cyan" />
              </div>
              <h3 className="font-bold">脉冲发光</h3>
              <p className="text-sm text-quantum-silver/60 mt-2">animate-pulse-glow-cyan</p>
            </div>
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-glitch-magenta/20 flex items-center justify-center animate-float">
                <Sparkles size={40} className="text-glitch-magenta" />
              </div>
              <h3 className="font-bold">漂浮动画</h3>
              <p className="text-sm text-quantum-silver/60 mt-2">animate-float</p>
            </div>
            <div className="glass-effect rounded-xl p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-quantum-silver/20 flex items-center justify-center breathe-glow">
                <Sparkles size={40} className="text-quantum-silver" />
              </div>
              <h3 className="font-bold">呼吸光效</h3>
              <p className="text-sm text-quantum-silver/60 mt-2">breathe-glow</p>
            </div>
          </div>
        </section>

        {/* 故障艺术 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">故障艺术效果</h2>
          <div className="glass-effect rounded-xl p-12 text-center">
            <h3 className="text-6xl font-bold glitch-effect" data-text="GLITCH EFFECT">
              GLITCH EFFECT
            </h3>
            <p className="text-quantum-silver/60 mt-4">鼠标悬停查看效果</p>
          </div>
        </section>

        {/* 终端框 */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">终端框样式</h2>
          <div className="glass-effect rounded-lg p-6 font-mono text-sm border-neon-cyan/30">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-glitch-magenta animate-pulse-glow-magenta"></div>
              <div className="w-3 h-3 rounded-full bg-neon-cyan/50"></div>
              <div className="w-3 h-3 rounded-full bg-quantum-silver/30"></div>
              <span className="text-quantum-silver/50 ml-2">SYSTEM_TERMINAL</span>
            </div>
            <div className="space-y-2 text-neon-cyan">
              <p><span className="text-quantum-silver/50">$</span> initialize_connection</p>
              <p className="text-quantum-silver/70">Connecting to timeline: 2142...</p>
              <p className="text-quantum-silver/70">Downloading consciousness...</p>
              <p className="text-neon-cyan">✓ Connection established</p>
              <p className="text-quantum-silver/70">Sync Rate: 100%</p>
              <p className="text-quantum-silver/70">Timeline Stability: Stable</p>
            </div>
          </div>
        </section>

        {/* 状态栏 */}
        <section>
          <h2 className="text-3xl font-bold mb-8 text-neon-cyan">状态栏样式</h2>
          <div className="glass-effect px-4 py-2 rounded-lg font-mono text-xs text-quantum-silver/70 border-neon-cyan/20 inline-block">
            <div className="flex items-center space-x-4">
              <span>Sync Rate: <span className="text-neon-cyan">100%</span></span>
              <span className="w-px h-4 bg-quantum-silver/30"></span>
              <span>Timeline Stability: <span className="text-neon-cyan">Stable</span></span>
              <span className="w-px h-4 bg-quantum-silver/30"></span>
              <span>Mission: <span className="text-glitch-magenta">Active</span></span>
            </div>
          </div>
        </section>
      </div>
    </div>
    </>
  )
}

export default DesignShowcase
